import { Accordion, AccordionDetails, AccordionSummary, Box, Card, Menu, MenuItem, Typography } from '@mui/material'
import { type SnapshotMember, type SnapshotDashboardModel } from '../../../../models/SnapshotDashboardModel'
import { ArrowForward, CameraAltOutlined, KeyboardArrowDown, MoreHoriz, PersonOutlined, PlagiarismOutlined, RocketLaunchOutlined, SendOutlined, SportsFootballOutlined } from '@mui/icons-material'
import { BlueButton } from '../../../customComponents/buttons/BlueButton'
import { useRef, useState } from 'react'
import { CancelSnapshot, CreateSnapshot, SendReminder, SendReminders, StartCETAAssessment } from '../../../../services/CETAClassroomService'
import { Modal } from '../../../customComponents/Modal'
import { toast } from 'react-toastify'
import { generatePath, useNavigate } from 'react-router'
import { AppRoutes } from '../../../Routes'
import { WarningCard } from '../../../customComponents/WarningCard'
import { useFinalizeSnapshot } from '../../../../hooks/use-finalize-snapshot'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

interface MemberAction {
  text: string
  icon: JSX.Element
  testId: string
  onClick: () => void
}

const SnapshotIcon = ({ isKickoff }: { isKickoff: boolean }): JSX.Element =>
  isKickoff ? <SportsFootballOutlined /> : <CameraAltOutlined />

interface Props {
  classroomId: string
  readonly: boolean
  isLead: boolean
  activeSnapshot: SnapshotDashboardModel | null
  completedSnapshots: SnapshotDashboardModel[]
  cetaUpdateBegin: Date
  refreshData: () => void
}

export const SnapshotsCard = (props: Props): JSX.Element => {
  const anchorEl = useRef<SVGSVGElement>(null)
  const nav = useNavigate()
  const [openMenu, setOpenMenu] = useState<boolean>(false)
  const [isCreatingSnapshot, setIsCreatingSnapshot] = useState<boolean>(false)
  const [isCancelingSnapshot, setIsCancelingSnapshot] = useState<boolean>(false)

  const { startFinalizing, finalizeModals } = useFinalizeSnapshot({
    classroomId: props.classroomId,
    activeSnapshot: props.activeSnapshot,
    completedSnapshots: props.completedSnapshots,
    cetaUpdateBegin: props.cetaUpdateBegin,
    refreshData: props.refreshData
  })

  const goToReconcileScreen = (snapshotId: number): void => {
    const route = generatePath(AppRoutes.CETA_RECONCILE,
      { classroomId: props.classroomId, snapshotId: snapshotId.toString() })
    nav(route)
  }

  const goToSnapshot = (): void => {
    const route = generatePath(AppRoutes.CETA_SNAPSHOT,
      { classroomId: props.classroomId, snapshotId: props.activeSnapshot?.id.toString() ?? '' })
    nav(route)
  }

  const startCETAAssessment = async (): Promise<void> => {
    await StartCETAAssessment(props.activeSnapshot?.id ?? 0)
    goToSnapshot()
  }

  const enterOnBehalf = async (member: SnapshotMember): Promise<void> => {
    await StartCETAAssessment(props.activeSnapshot?.id ?? 0, member.id)
    const route = generatePath(AppRoutes.CETA_SNAPSHOT,
      { classroomId: props.classroomId, snapshotId: props.activeSnapshot?.id.toString() ?? '' })
    nav(`${route}?forUserId=${member.id}`)
  }

  const sendReminder = async (member: SnapshotMember): Promise<void> => {
    await SendReminder(props.classroomId, member.id)
    toast.success('Reminder sent')
    setOpenMenu(false)
  }

  const sendReminders = async (): Promise<void> => {
    await SendReminders(props.classroomId)
    toast.success('Reminders sent')
    setOpenMenu(false)
  }

  const cancelSnapshot = async (): Promise<void> => {
    await CancelSnapshot(props.activeSnapshot?.id ?? 0)
    toast.success('Snapshot removed')
    props.refreshData()
  }

  const createSnapshot = async (): Promise<void> => {
    await CreateSnapshot(props.classroomId)
    toast.success('Snapshot created')
    props.refreshData()
  }

  const ellipsesClicked = (e: React.MouseEvent): void => {
    setOpenMenu(!openMenu)
    e.stopPropagation()
  }

  const startFinalizingSnapshot = (): void => {
    startFinalizing()
    setOpenMenu(false)
  }

  const memberActions = (member: SnapshotMember): MemberAction[] => {
    if (!member.isCurrentMember) {
      if (member.hasCompleted) {
        return []
      }

      const actions: MemberAction[] = []
      // A lead can enter (or resume) on behalf of a member who has not worked on it themselves.
      if (!member.startedBySelf) {
        actions.push({
          text: member.hasStarted ? 'Continue on Their Behalf' : 'Enter on Their Behalf',
          icon: <ArrowForward />,
          testId: 'enterOnBehalf',
          onClick: () => { void enterOnBehalf(member) }
        })
      }
      actions.push({ text: 'Send Reminder', icon: <SendOutlined />, testId: 'sendReminder', onClick: () => { void sendReminder(member) } })
      return actions
    }

    if (member.hasCompleted) {
      return [{ text: 'Review My Responses', icon: <ArrowForward />, testId: 'reviewMyResponses', onClick: goToSnapshot }]
    }

    return [member.hasStarted
      ? { text: 'Continue', icon: <ArrowForward />, testId: 'continueAssessment', onClick: goToSnapshot }
      : { text: 'Get Started', icon: <ArrowForward />, testId: 'startAssessment', onClick: () => { void startCETAAssessment() } }]
  }

  const submissionCount = props.activeSnapshot?.completedMemberNames.length ?? 0
  const hasIncompleteMembers = (props.activeSnapshot?.incompleteMemberNames.length ?? 0) > 0

  const hasCompletedUpdate = props.completedSnapshots.some(s => s.isCETAUpdate)
  const canAddCETAUpdate = props.cetaUpdateBegin < new Date() && !hasCompletedUpdate
  const placeholderIsKickoff = props.completedSnapshots.length === 0 && !canAddCETAUpdate
  const newSnapshotNumber = props.completedSnapshots.filter(s => !s.isCETAUpdate).length
  const placeholderName = canAddCETAUpdate
    ? 'CETA Update'
    : placeholderIsKickoff ? 'CETA Kickoff' : `Snapshot ${newSnapshotNumber}`

  return <Card sx={{ backgroundColor: '#F3F3F380', minHeight: '3em', width: '300px', border: '1px solid var(--border)', p: '1em' }}>
    <Typography variant='h5' textAlign='center' fontWeight='600'>
      CETA Snapshots
    </Typography>

    {(props.activeSnapshot != null || (props.isLead && !props.readonly)) &&
      <Typography variant='body1' textAlign='center' my='1em'>
        Active Snapshot
      </Typography>
    }

    {props.activeSnapshot == null && props.isLead && !props.readonly &&
      <Accordion data-testid='placeholderSnapshot' defaultExpanded={true}>
        <AccordionSummary expandIcon={<KeyboardArrowDown sx={{ color: 'white', bgcolor: '#726F74', width: '1.125em', height: '1.125em', borderRadius: '50%' }} data-testid='expand' />}>
          <Box sx={{ display: 'flex' }}>
            <SnapshotIcon isKickoff={placeholderIsKickoff} />
            <Typography variant='body1' textAlign='center' ml='1em'>
              {placeholderName}
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <BlueButton onClick={() => { setIsCreatingSnapshot(true) }} sx={{ width: '100%', justifyContent: 'space-between' }} dataTestId='createSnapshot'>
            Get Started
            <RocketLaunchOutlined />
          </BlueButton>
        </AccordionDetails>
      </Accordion>
    }

    {props.activeSnapshot != null &&
      <Accordion data-testid='activeSnapshot' defaultExpanded={true}>
        <AccordionSummary
          expandIcon={
            <KeyboardArrowDown
              data-testid='expand'
              sx={{
                color: 'white',
                bgcolor: '#726F74',
                width: '1.125em',
                height: '1.125em',
                borderRadius: '50%'
              }}
            />
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SnapshotIcon isKickoff={props.activeSnapshot.isKickoff} />

            <Typography variant='body1' textAlign='center' ml='1em'>
              {props.activeSnapshot.name}
            </Typography>

            {hasIncompleteMembers &&
              <ErrorOutlineIcon sx={{ ml: '1em' }} />
            }
          </Box>

          {props.isLead && !props.readonly &&
            <MoreHoriz
              aria-label="menu"
              ref={anchorEl}
              fontSize='large'
              data-testid="snapshotMenu"
              onClick={ellipsesClicked}
              sx={{ fill: 'black', ml: '.5em' }}
            />
          }
        </AccordionSummary>

        <AccordionDetails>
          {props.activeSnapshot.members.map(member => {
            const actions = memberActions(member)
            return <Box key={member.id}>
              { !member.hasCompleted &&
                <WarningCard message={`This team member still needs to contribute to ${props.activeSnapshot?.name ?? ''}.`} />
              }
              {actions.length > 0 &&
                <Accordion sx={{ my: '.625em !important' }} data-testid='snapshotMember' defaultExpanded={true}>
                  <AccordionSummary expandIcon={<KeyboardArrowDown data-testid='expand' />}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonOutlined />

                      <Box ml='.625em'>
                        <Typography variant='body1' textAlign='center' sx={{ fontWeight: '500' }}>
                          {member.name}
                        </Typography>
                        <Typography variant='body1' textAlign='center' sx={{ fontSize: '.875em' }}>
                          {member.title}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  {!props.readonly &&
                    <AccordionDetails>
                      {actions.map((action, index) =>
                        <BlueButton key={action.testId} onClick={action.onClick} sx={{ width: '100%', justifyContent: 'space-between', mb: index < actions.length - 1 ? '.5em' : 0 }} dataTestId={action.testId}>
                          {action.text}
                          {action.icon}
                        </BlueButton>
                      )}
                    </AccordionDetails>
                  }
                </Accordion>
              }
            </Box>
          })}

          <Menu open={openMenu} anchorEl={anchorEl.current} onClose={() => { setOpenMenu(false) }}>
            {submissionCount > 0 &&
              <MenuItem
                data-testid='menuItem-finalizeSnapshot'
                onClick={startFinalizingSnapshot}
              >
                Finalize Snapshot
              </MenuItem>
            }

            {props.activeSnapshot.members.some(member => member.isCurrentMember && member.hasCompleted) &&
              <MenuItem
                data-testid='menuItem-reviewMyResponses'
                onClick={goToSnapshot}
              >
                Review My Responses
              </MenuItem>
            }

            {props.activeSnapshot.members.some(member => !member.isCurrentMember && !member.hasCompleted) &&
              <MenuItem
                data-testid='menuItem-sendReminders'
                onClick={sendReminders}
              >
                Send Reminders
              </MenuItem>
            }

            <MenuItem
              data-testid='menuItem-cancelSnapshot'
              onClick={() => { setIsCancelingSnapshot(true) }}
            >
              Cancel Snapshot
            </MenuItem>
          </Menu>
        </AccordionDetails>
      </Accordion>
    }

    {props.completedSnapshots.length > 0 &&
      <Typography variant='body1' textAlign='center' mt='1em'>
        Completed Snapshots
      </Typography>
    }

    {props.completedSnapshots.map((snapshot) =>
      <Box
        key={snapshot.name}
        data-testid='completedSnapshots'
        sx={{
          backgroundColor: 'white',
          border: '1px solid var(--border)',
          display: 'flex',
          p: '16px',
          mb: '.625em',
          justifyContent: 'space-between'
        }}
      >
        <Box display='flex'>
          <SnapshotIcon isKickoff={snapshot.isKickoff} />

          <Typography variant='body1' textAlign='center' ml='1em'>
            {snapshot.name}
          </Typography>
        </Box>

        <PlagiarismOutlined onClick={() => { goToReconcileScreen(snapshot.id) }} sx={{ float: 'right', cursor: 'pointer' }} data-testid='reviewCompletedSnapshot' />
      </Box>
    )}

    {isCreatingSnapshot &&
      <Modal
        open={true}
        title='Create Snapshot'
        bodyContent='This will create a new CETA Snapshot and assign it to each of the team members in this CETA classroom. Are you ready to begin this process?'
        confirmButtonText='Yes, Continue'
        cancelButtonText='No, Cancel'
        onClose={() => { setIsCreatingSnapshot(false) }}
        onConfirm={createSnapshot}
      />
    }

    {isCancelingSnapshot &&
      <Modal
        open={true}
        title='Cancel Snapshot'
        bodyContent={'This action will remove the snapshot and any team members\' responses. Are you sure you want to continue?'}
        confirmButtonText='Yes, Cancel Snaphsot'
        cancelButtonText='No, Exit'
        onClose={() => { setIsCancelingSnapshot(false) }}
        onConfirm={cancelSnapshot}
      />
    }

    {finalizeModals}
  </Card>
}
